import { useState } from 'react';
import { UploadUrlDocument, useUploadUrlLazyQuery } from '../../generated/types';
import { resizeImage } from '../../shared/utils/file/image';
import { merge } from 'lodash';
import axios from 'axios';
import client from '../apollo/client';

export type UseUploadOptions = {
    maxSize: number;
    requestId: string;
    onUploadProgress?: (percent: number | null) => void;
};

const defaultOptions: UseUploadOptions = {
    maxSize: 10 * 1024 * 1024,
    requestId: 'uploadImage',
};
export const useUpload = (rootOptions?: Partial<UseUploadOptions>) => {
    const [isLoading, setIsLoading] = useState(false);

    const uploadImage = async (file: File, options?: Partial<UseUploadOptions>): Promise<string | undefined> => {
        const { maxSize, onUploadProgress } = merge(options, rootOptions, defaultOptions);
        setIsLoading(true);
        const resizedImage = await resizeImage(file);

        // Validate size
        if (resizedImage && file.size > maxSize) {
            throw new Error('File too large. Please upload a smaller image.');
        }

        onUploadProgress && onUploadProgress(0);

        const res = await client.query({
            query: UploadUrlDocument,
            variables: { fileName: file.name, contentType: file.type },
            fetchPolicy: 'network-only',
        });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (res && res.errors) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            throw new Error(res.errors?.message[0] || 'Can not upload file');
        }
        if (res && res.data) {
            const uploadUrl = res?.data.UploadUrl.url;

            try {
                if (uploadUrl) {
                    await axios.put(uploadUrl, resizedImage, {
                        headers: {
                            'Content-Type': file.type,
                        },
                        onUploadProgress: (e) => {
                            onUploadProgress && onUploadProgress(Math.round((100 * e.loaded) / e.total));
                        },
                    });

                    const uploadUrlSplit = uploadUrl.split('?');
                    if (uploadUrlSplit) {
                        return uploadUrlSplit[0];
                    }
                }
            } catch (error) {
                setIsLoading(false);
                throw new Error('Can not upload file');
            }
        }

        setIsLoading(false);
        onUploadProgress && onUploadProgress(null);
    };

    return {
        uploadImage,
        isLoading,
    };
};
